<template>
	<div class="document-packet ignore-deal-lock">
		<IsBusyScreenComponent v-if="isPacketBusy" />
		<div
			v-if="packet"
			class="document-packet-container"
		>
			<div
				v-show="showTitle || (showButtons && packet.isCurrent)"
				class="document-packet-title-container"
				:style="$grid('2-1-1')"
			>
				<div
					v-show="showTitle"
					class="document-packet-subtitle"
				>
					<span>Packet generated by {{ packet.generatedBy.name }} - {{ packet.packetDate }}</span>
				</div>
			</div>

			<div class="document-packet-grid-container">
				<Panel
					class="paperwork-categories-panel"
					:action="getDocumentsPanelActions()"
				>
					<template #HeaderComponent>
						<div class="panel-buttons">
							<div
								v-if="showButtons && packet.isCurrent"
								class="panel-header-button-array"
								style="display: flex; align-items: center"
							>
								<ButtonLoading
									class="button button-icon-text button-accept"
									:disabled="isActivatePaperworkButtonDisabled || isConnectedToCustomer"
									:isLoading="busyActivating"
									loadingText="Activating"
									@click="() => activatePaperwork()"
								>
									<i class="fas fa-magic" />
									{{ isConnectedToCustomer ? 'Please disconnect Customer to Activate' : 'Activate' }}
								</ButtonLoading>

								<ButtonLoading
									class="button button-icon-text button-unwind"
									:disabled="isVoidPaperworkButtonDisabled"
									:isLoading="busyVoiding"
									loadingText="Voiding"
									@click="() => voidPaperworkAction()"
								>
									<i class="fas fa-ban" /> Void
								</ButtonLoading>

								<button
									class="button button-icon-text button-load"
									:disabled="isOpenPaperworkButtonDisabled"
									@click="openCustomerPaperwork()"
								>
									<i
										v-if="$meetingHub.connected"
										class="fas fa-file-signature"
									/>

									{{ isConnectedToCustomer ? 'Sign' : 'Please Connect Customer to Sign' }}
								</button>

								<DropdownButton
									v-if="$global.isAdminView"
									:label="`Admin Actions ${totalSelectedDocuments ? '(' + totalSelectedDocuments + ')' : ''}`"
									icon="fas fa-ban"
									class="document-packet-button-dropdown"
									:actions="adminAction"
								/>

								<button
									v-else
									class="button button-icon-text button-load view-button"
									:disabled="Object.values(rawSelectedDocuments).length <= 0"
									@click="handleRepopulate()"
								>
									<i class="far fa-eye" />
									<span>View Selected</span>
								</button>
							</div>
						</div>
					</template>

					<div
						class="collapse-all-stack"
						@click="toggleCollapseExpandAllCategories()"
					>
						<span class="fa-stack fa-1x stack-container">
							<i class="far fa-square fa-stack-1x stack-border"></i>
							<i class="fas fa-square fa-stack-1x stack-background"></i>

							<i
								v-if="anyCategoryCollapsed"
								class="fas fa-plus-square fa-stack-1x stack-sign"
								style="color: var(--main-color)"
							>
							</i>
							<i
								v-else
								class="fas fa-minus-square fa-stack-1x stack-sign"
								style="color: var(--main-color)"
							></i>
						</span>

						<InputCheckbox
							id="select-all-checkbox"
							:isControlled="true"
							@click="toggleSelectRichtables"
							:modelValue="anyCategorySelected"
							:useSquare="true"
						/>

						<label style="margin: unset; font-size: 20px; font-weight: bold">Documents</label>
					</div>

					<div class="paperwork-categories-wrapper">
						<RichTable
							v-for="(category, index) in paperworkCategories"
							:key="index"
							ref="richtable_refs"
							selectable="checkbox"
							:tableData="getCategoryData(category)"
							:headers="getHeaders(category.description)"
							:selectionKey="(doc: any) => category.id + '-' + doc._index"
							:useSquareInputCheckbox="true"
							:isCollapsable="true"
							:isSelectDisabled="(doc: any) => !canSelectDocument(doc)"
							:tableRowStyle="(doc: any) => getTableRowStyle(doc)"
							@selectionChanged="listOfDocs => toggleSelect(category, listOfDocs)"
						>
							<template #docName="doc">
								<DocumentPacketDocumentName
									:doc="doc._ref"
									:fimenu="fimenu"
									:packet="packet"
									:getProductDetails="getProductDetails"
									:getPaperworkDocType="getPaperworkDocType"
								/>
							</template>

							<template #signature="doc">
								<DocumentPacketDocumentSignature
									v-if="doc.documentGenerationType == ENUMS.DocumentGenerationType.Paperwork"
									:doc="doc._ref"
									:signedInformation="signedInformation"
									:fimenu="fimenu"
								/>

								<div
									v-else
									class="center-content"
								>
									<div class="text-success defaultCursor signersText">
										<i class="fa fa-check-circle iconSpacing"></i>
										<span>N/A</span>
									</div>
								</div>
							</template>

							<template #activation="doc">
								<DocumentPacketDocumentActivation
									:fimenu="fimenu"
									:doc="doc._ref"
									:tooltipInformation="tooltipInformation"
									:user="user"
									:openEditContractNumber="openEditContractNumber"
								/>
							</template>

							<template #download="doc">
								<DocumentPacketActions
									:doc="doc._ref"
									:downloadDocument="downloadDocument"
									:paperworkBusy="paperworkBusy || fimenu.isFinalizingPaperwork || isViewingDoc"
									:isCurrent="packet.isCurrent"
									:canSelectDocument="canSelectDocument"
									:openPreviewModalManyDocs="openPreviewPDFModalManyDocs"
									:openPreviewModalSingleDoc="openPreviewPDFModalSingleDoc"
									:regenerateDocument="regenerateDocument"
								/>
							</template>
						</RichTable>
					</div>
					<div class="paperwork-categories-wrapper">
						<RichTable
							:tableData="fafaDoc()"
							:headers="getHeaders('Summary')"
						>
							<template #docName="summary">
								<DocumentPacketDocumentName
									:doc="summary._ref"
									:fimenu="fimenu"
									:getProductDetails="getProductDetails"
									:getPaperworkDocType="getPaperworkDocType"
								/>
							</template>

							<template #signature>
								<div class="center-content">
									<div class="text-success defaultCursor signersText">
										<i class="fa fa-check-circle iconSpacing"></i>
										<span>N/A</span>
									</div>
								</div>
							</template>

							<template #activation="summary">
								<DocumentPacketDocumentActivation
									:fimenu="fimenu"
									:doc="summary._ref"
									:tooltipInformation="tooltipInformation"
									:user="user"
									:openEditContractNumber="openEditContractNumber"
								/>
							</template>

							<template #download="summary">
								<button
									:class="{ 'ignore-deal-locks': !paperworkBusy }"
									style="border-radius: 5px; width: 55px"
									@click="() => openPreviewSummaryModal(summary)"
									:disabled="paperworkBusy"
								>
									<i class="fas fa-eye"></i>
								</button>
							</template>
						</RichTable>
					</div>
				</Panel>
			</div>
		</div>

		<div
			v-else
			style="padding-left: 20px"
		>
			No Paperwork
		</div>
	</div>
</template>
<script lang="ts">
	import { defineComponent, PropType } from 'vue';
	import moment from 'moment';

	import { notifyIfAnyDealChargebacks, updateInventory } from '@core/classes/FIMenuHelper';
	import $modal from '@core/services/modal';
	import api from '@core/services/api/_api';
	import auth from '@core/services/auth';
	import settings from 'settings';
	import util from '@core/services/util';
	import { FinalizeEContract } from '@core/helpers/finance-channels-helper';

	import { CancelPlateTransaction, IsPlateTransactionsEnabled, IssuePlateRegistrationTransfer, IssueTemporaryTag } from '@/helpers/plate-transactions-helper';
	import DocumentAssociation from '@core/classes/DocumentAssociation';
	import { IDocumentOverlay } from '@core/classes/Paperwork/DocumentOverlay';
	import ENUMS from '@core/classes/Enums';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuCreditReport from '@core/classes/FIMenuCreditReport';
	import FIMenuPaperwork from '@core/classes/FIMenuPaperwork';
	import FIMenuPlateTransactions from '@core/classes/FIMenuPlateTransactions';
	import FIMenuProduct from '@core/classes/FIMenuProduct';
	import IDocumentType from '@core/_interfaces/DocumentType';
	import IPaperworkCategory from '@core/_interfaces/PaperworkCategory';
	import { IPaperworkDocument } from '@core/classes/Paperwork/PaperworkDocument';
	import { IPaperworkPacket } from '@core/classes/Paperwork/PaperworkPacket';
	import { IUploadedDocument } from '@core/classes/UploadedDocument';
	import { PlateTransactionType } from '@core/classes/SharedEnums';
	import { isDocSignedBySignerOrDone, isDocWetSignOnly } from '@core/helpers/esign-helper';

	import modalEditContractNumber from '@/modals/modalEditContractNumber.vue';
	import modalInfo from '@core/modals/modalInfo.vue';
	import modalLegalTerm from '@core/modals/modalLegalTerm.vue';
	import modalPreviewPDF from '@core/modals/modalPreviewPDF.vue';
	import modalSetControlNumber from '@/modals/modalSetControlNumber.vue';

	import ButtonLoading from '@core/components/ButtonLoading.vue';
	import DropdownButton from '@core/components/DropdownButton.vue';
	import InputCheckbox from '@core/components/InputCheckbox.vue';
	import IsBusyScreenComponent from '@core/components/IsBusyScreenComponent.vue';
	import Panel from '@core/components/Panel.vue';
	import RichTable from '@core/components/RichTable.vue';

	import DocumentPacketActions from '@/components/document-packet/DocumentPacketActions.vue';
	import DocumentPacketDocumentActivation from '@/components/document-packet/DocumentPacketDocumentActivation.vue';
	import DocumentPacketDocumentName from '@/components/document-packet/DocumentPacketDocumentName.vue';
	import DocumentPacketDocumentSignature from '@/components/document-packet/DocumentPacketDocumentSignature.vue';
	import { EContractStatus } from '@core/classes/EContract';
	import { UserPayload } from '@core/helpers/user-helper';

	export interface IDocumentPacketCategory extends IPaperworkCategory {
		docs: IPacketDocument[];
		uploadedDocuments: any[];
		isOpen: boolean;
		isCategory: boolean;
	}

	export interface IPacketDocument extends IPaperworkDocument {
		/** ENUMS.DocumentGenerationType */
		documentGenerationType?: number;

		/** Used to be set by the customer meeting websocket, but that functionality is currently broken. */
		isOpen?: boolean;
	}

	export interface IUploadedDocumentWithMeta extends IUploadedDocument {
		display: string;
		documentPacketCategoryId: string;
	}

	export default defineComponent({
		name: 'DocumentPacket',
		components: {
			Panel,
			RichTable,
			InputCheckbox,
			DropdownButton,
			DocumentPacketActions,
			DocumentPacketDocumentActivation,
			DocumentPacketDocumentSignature,
			DocumentPacketDocumentName,
			IsBusyScreenComponent,
			ButtonLoading,
		},
		props: {
			packet: Object as PropType<IPaperworkPacket>,
			fimenu: Object as PropType<FIMenu>,
			showTitle: Boolean,
			showButtons: Boolean,
			modalStatus: Object as PropType<{ modalBusy: boolean }>,
			setVoidedPacket: Function as PropType<() => void>,
			restorePacket: Function as PropType<(packet: IPaperworkPacket) => Promise<void>>,
		},
		data() {
			return {
				docHeaders: [
					{
						name: 'documentName',
						display: 'Document Name',
						slot: 'docName',
						cssClass: 'docName-header',
					},
					{
						name: 'signature',
						display: 'Digitally Signed',
						slot: 'signature',
						cssClass: 'signature-header',
					},
					{
						name: 'activation',
						display: 'Contract #',
						slot: 'activation',
						cssClass: 'activation-header',
					},
					{
						name: 'download',
						display: '',
						sortable: false,
						slot: 'download',
						cssClass: 'download-header',
					},
				],
				paperworkBusy: false,
				busyVoiding: false,
				busyActivating: false,
				busyIssuingPlateReg: false,
				paperworkCategories: [] as Partial<IDocumentPacketCategory>[],
				otherCategoryDescription: 'Other',
				uploadedDocumentTypes: settings.lookups.uploadedDocumentTypes,
				rawSelectedDocuments: {} as Record<string, IPacketDocument[]>,
				richTableRefs: null as any[] | any,
				regeneratingAndDownloadMultipleDocs: false,
				allTablesCollapsed: false,
				isLoading: false,
				isViewingDoc: false,
			};
		},
		computed: {
			isConnectedToCustomer(): boolean {
				return !!this.$meetingHub.code;
			},
			isPacketBusy(): boolean {
				return this.regeneratingAndDownloadMultipleDocs || this.isLoading || this.fimenu.isFinalizingPaperwork || this.busyIssuingPlateReg;
			},
			isActivatePaperworkButtonDisabled(): boolean {
				return (
					this.paperworkBusy || this.fimenu.paperworkStatus === ENUMS.PAPERWORK_STATUS.ACTIVATED || !this.user.AllowDealActivation || this.fimenu.isSpectator
				);
			},
			isVoidPaperworkButtonDisabled(): boolean {
				return this.paperworkBusy || this.fimenu.isSpectator || !this.user.AllowDealActivation || !this.fimenu.isInFinanceDepartment;
			},
			isOpenPaperworkButtonDisabled(): boolean {
				return this.paperworkBusy || !this.$meetingHub.connected || !this.$meetingHub.code || this.areAllOverlaysSigned() || this.fimenu.isSpectator;
			},
			anyCategoryCollapsed(): boolean {
				if (Array.isArray(this.richTableRefs)) {
					return this.richTableRefs.some(table => table?.data?.isCollapsed);
				} else {
					return !!this.richTableRefs?.data?.isCollapsed;
				}
			},
			anyCategorySelected(): boolean {
				if (Array.isArray(this.richTableRefs)) {
					return !this.richTableRefs.some(table => !table?.data?.selectAllCheckbox);
				} else {
					return !!this.richTableRefs?.data?.selectAllCheckbox;
				}
			},
			totalSelectedDocuments(): number {
				return this.selectedDocuments.packetDocuments.length + this.selectedDocuments.unknown.length + this.selectedDocuments.uploadedDocuments.length;
			},
			util(): any {
				return util;
			},
			ENUMS(): any {
				return ENUMS;
			},
			hasOtherDocuments(): boolean {
				return !!this.fimenu.paperwork?.uploadedDocuments?.some((doc: any) => doc.associations.some((asoc: DocumentAssociation) => asoc.type == 20));
			},
			user(): UserPayload {
				return auth.getTokenPayload();
			},
			isManualFinalizeRequired(): boolean {
				//If FinalizedPaperwork gets interrupted before finish, there should be no doc the is filled and signed
				return this.packet.documents.every(d => !d.signedDoc);
			},
			daysUntilCanResubmit(): number {
				// Get latest temp tag

				const mostRecentTempTransfer = this.fimenu.vehicle.registrationInfo.plateTransactions
					.filter(x => x.type == PlateTransactionType.NewTempPlate)
					.sort((a, b) => new Date(b.dateSubmitted).getTime() - new Date(a.dateSubmitted).getTime())[0];
				if (mostRecentTempTransfer == null) return 0;

				return 25 + util.toMoment(mostRecentTempTransfer.dateSubmitted).diff(moment(), 'days');
			},
			exceededMaximumRequestAmount(): boolean {
				const actualPlateTransaction = this.fimenu.getActualDealPlateTransactions();

				const amountOfRequests = this.fimenu.vehicle.registrationInfo.plateTransactions.reduce((prev, cur) => {
					const driverLicenseSame = cur.otherInfo.driversLicense == actualPlateTransaction.otherInfo.driversLicense;
					const vehicleVinSame = cur.otherInfo.vehicleVin == actualPlateTransaction.otherInfo.vehicleVin;
					const isTempTag = cur.type == PlateTransactionType.NewTempPlate;

					if (driverLicenseSame && vehicleVinSame && isTempTag) prev += 1;

					return prev;
				}, 0);

				return amountOfRequests >= 2;
			},
			disableDMVRequest(): boolean {
				if (this.$global.isAdminView) return false;

				if (this.daysUntilCanResubmit > 0 || this.exceededMaximumRequestAmount || this.busyIssuingPlateReg) return true;

				return false;
			},
			plateTransactionEnabled(): boolean {
				return IsPlateTransactionsEnabled(this.fimenu);
			},
			missingPlateDocuments() {
				if (!IsPlateTransactionsEnabled(this.fimenu)) return false;
				const actualDeal = this.fimenu.getActualDealPlateTransactions();

				if (!actualDeal) return false;

				return !this.fimenu.paperwork.uploadedDocuments?.some((doc: any) =>
					doc.associations.some((asoc: DocumentAssociation) => {
						return asoc.id == actualDeal.providerDealId;
					}),
				);
			},
			selectedDocuments(): {
				uploadedDocuments: any[];
				packetDocuments: IPacketDocument[];
				unknown: any[];
			} {
				return Object.values(this.rawSelectedDocuments).reduce(
					(documentsList, listOfDocs) => {
						listOfDocs.forEach(doc => {
							let targetList = null;
							switch (doc.documentGenerationType) {
								case ENUMS.DocumentGenerationType.Paperwork:
									targetList = documentsList.packetDocuments;
									break;
								case ENUMS.DocumentGenerationType.Uploaded:
									targetList = documentsList.uploadedDocuments;
									break;
								default:
									targetList = documentsList.unknown;
									break;
							}
							targetList.push(doc);
						});
						return documentsList;
					},
					{ uploadedDocuments: [], packetDocuments: [], unknown: [] },
				);
			},
			flatSelectedDocuments(): IPacketDocument[] {
				return Object.values(this.rawSelectedDocuments).reduce((allDocuments, listOfDocs) => {
					listOfDocs.forEach(doc => {
						allDocuments.push(doc); // Directly push each document into the resulting array
					});
					return allDocuments;
				}, []); // Initialize as an empty array to collect documents
			},
			activeProducts(): FIMenuProduct[] {
				const hasSelectedATerm = this.fimenu.coverageTerms.find(term => term.selected);

				if (!hasSelectedATerm) {
					return [];
				}

				if (!this.packet.isCurrent) {
					return this.packet.acceptList ?? [];
				}

				return this.fimenu.getSelectedCoverageTerm().getAcceptList(this.fimenu.dealType, this.fimenu.getSelectedDealTerm()?.term);
			},
			plateTransactionsActions(): any[] {
				const actions = [];

				actions.push({
					label: `Generate Temp. Tag.`,
					class: 'button',
					click: this.generateTempTag,
					disabled: () => this.disableDMVRequest,
				});

				// Check if we have any successful transfer.
				// To do so, we check if we have anything with transfer type and does NOT have any error.
				const haveSuccessfulTransfer = this.fimenu.vehicle?.registrationInfo.plateTransactions.some(
					x => x.type == ENUMS.PLATE_TRANSACTIONS_TYPES.Transfer && !x.hasError(),
				);

				// If we're meant to transfer and we don't have any transfer.
				// Add the button for retry.
				if (this.fimenu.vehicle.registrationInfo.isPlateTransfer && !haveSuccessfulTransfer) {
					actions.push({
						label: `Transfer Plate`,
						class: 'button',
						click: this.transferRegPlate,
						disabled: () => this.busyIssuingPlateReg,
					});
				}

				// IAdmin
				// Add the button for retry.

				return actions;
			},
			adminAction(): any[] {
				const actions = [
					{
						label: `View Selected`,
						class: 'button',
						click: this.handleRepopulate,
						disabled: () => this.totalSelectedDocuments <= 0,
					},
					{
						label: `Repopulate Selected`,
						class: 'button-save',
						click: () => this.handleRepopulate(false),
						disabled: () => this.totalSelectedDocuments <= 0 || this.fimenu.isSpectator,
					},
					//THE FILTER THAT REPLACES THE DOC IS FILTERING OUT ALL OF TIME (LIKE TITLE DOCS)
					{
						label: `Regenerate Selected`,
						class: 'button-info',
						click: this.regenerateSelected,
						disabled: () => this.totalSelectedDocuments <= 0 || this.fimenu.isSpectator,
					},
					{
						label: `Delete Selected`,
						class: 'button-unwind',
						click: this.handleRemoveDocuments,
						disabled: () => this.totalSelectedDocuments <= 0 || this.fimenu.isSpectator,
					},
					{
						label: `Refill Packet`,
						class: 'button-load',
						click: this.handleRefreshPacket,
						disabled: () => this.fimenu.isSpectator,
					},
					{
						label: 'Void Documents Only',
						class: 'button-unwind',
						click: () => this.voidPaperworkAction(true),
						disabled: () => this.fimenu.isSpectator,
					},
					//Remove Delay for selected Docs To Admin Activate
					{
						label: 'Ignore Delay & Activate',
						class: 'button',
						click: () => this.ignoreDelayActivate(),
						disabled: () => this.totalSelectedDocuments <= 0 || this.fimenu.isSpectator,
					},
				];

				const hasAnyPlateTransaction = this.fimenu.vehicle?.registrationInfo?.plateTransactions?.length <= 0;

				if (this.plateTransactionEnabled || hasAnyPlateTransaction) {
					actions.push({
						label: `Get Plate Docs`,
						class: 'button',
						click: this.getMissingPlateDocuments,
						disabled: () => this.busyIssuingPlateReg,
					});
				}

				if (this.fimenu.isEContracting && this.fimenu.dealJacket.eContract.id != null) {
					actions.push({
						label: `E-Contract`,
						class: 'button',
						click: () => FinalizeEContract(this.fimenu),
						disabled: () => this.fimenu.isSpectator,
					});
				}

				return actions;
			},

			isAnyDocPendingActivation(): boolean {
				return this.packet.documents.some(d => d.activationInfo?.dateToActivate != null);
			},
			docTypesToFilterForRecent(): string[] {
				return settings.lookups.uploadedDocumentTypes.filter((t: IDocumentType) => t.onlyShowMostRecent).map((t: IDocumentType) => t.code);
			},
		},
		watch: {
			'fimenu.isFinalizingPaperwork'(newValue) {
				// Unselect all the paperwork once we start finalizing.
				if (newValue) this.unSelectAll();
			},
			packet: {
				handler: function () {
					this.setPaperworkCategories();
				},
				deep: true,
			},
		},
		created() {
			if (this.packet) {
				this.packet.packetDate = this.convertDateStringToDate(this.packet.packetDateStamp);
				this.fimenu.paperwork.orderPacketDocuments(this.packet);
				this.setPaperworkCategories();
			}
		},
		beforeUnmount() {
			this.EventBus.off('paperworkReset', this.paperworkResetEmitterHandler);
		},
		mounted() {
			this.EventBus.on('paperworkReset', this.paperworkResetEmitterHandler);
			this.richTableRefs = (this.$refs.richtable_refs as any[])?.map(r => util.getVueRefToWatch(r));
		},
		methods: {
			fafaDoc(): any[] {
				// Testing - update later to use the actual data
				return [
					{
						'description': 'Electronic Evidence Summary', // Title
						'filledDoc': '315a4fd8-5202-4cec-ab05-b73209594870/20241113_212545/FilledDocuments/PaperworkDocument-0350958e-0783-4d4d-bd89-a6da79957bcc.pdf', // Needed for downlaod
						'signedDoc': null, // Needed for downlaod
						'type': 0, //shows eye for download
						'documentGenerationType': 0,
						'status': 30, // 30 = Signed
					},
				];
			},
			handleFinalizePaperwork() {
				this.EventBus.emit('allPaperworkSigned');
			},
			paperworkResetEmitterHandler() {
				this.unSelectAll();
			},
			getTableRowStyle(doc: IPacketDocument): string {
				if (doc.isOpen) {
					return 'border-left-row';
				} else {
					return null;
				}
			},
			canSelectDocument(doc: IPacketDocument | any): boolean {
				//Documents status that can't be selected.
				if (doc.status == ENUMS.PaperworkDocumentStatus.ErrorGenerating) return false;

				// Type of document that can't be select.
				if (doc.documentGenerationType == ENUMS.DocumentGenerationType.Paperwork) {
					return true;
				} else if (doc.documentGenerationType == ENUMS.DocumentGenerationType.Uploaded) {
					return doc.docInfo.fileType.includes('image'); //only imgs for now due to protected pdfs. || doc.docInfo.fileType.includes("pdf")
				}

				return false;
			},
			getHeaders(headerName: string): any[] {
				return [
					{
						name: 'documentName',
						display: headerName,
						slot: 'docName',
						cssClass: 'docName-header',
					},
					{
						name: 'signature',
						display: headerName === 'Requires Wet Signature' ? 'Signed' : 'Digitally Signed',
						slot: 'signature',
						cssClass: 'signature-header',
					},
					{
						name: 'activation',
						display: 'Contract #',
						slot: 'activation',
						cssClass: 'activation-header',
					},
					{
						name: 'download',
						display: '',
						sortable: false,
						slot: 'download',
						cssClass: 'download-header',
					},
				];
			},
			getCategoryData(category: Partial<IDocumentPacketCategory>): (IPaperworkCategory | any)[] {
				// We're doing this "hack" because we don't have a "solid" way to identify the documents.

				category?.docs?.forEach(doc => {
					doc.documentGenerationType = ENUMS.DocumentGenerationType.Paperwork;
				});

				category?.uploadedDocuments?.forEach(doc => {
					doc.documentGenerationType = ENUMS.DocumentGenerationType.Uploaded;
				});

				return [...category.docs, ...category.uploadedDocuments];
			},
			toggleSelectRichtables() {
				if (Array.isArray(this.richTableRefs)) {
					const allCategoriesSelected = this.richTableRefs.every(table => {
						return table.data.selectAllCheckbox;
					});

					this.richTableRefs.forEach(table => {
						if (allCategoriesSelected) table.methods.unSelectAll();
						else table.methods.selectAll();
					});
				} else {
					if (this.richTableRefs?.data?.selectedCheckbox?.length) this.richTableRefs.methods.selectAll();
					else this.richTableRefs.data.unSelectAll();
				}
			},
			selectAll() {
				this.richTableRefs?.forEach((richtableRef: any) => {
					richtableRef.methods.selectAll();
				});
			},
			unSelectAll() {
				this.richTableRefs?.forEach((richtableRef: any) => {
					richtableRef.methods.unSelectAll();
				});
			},
			toggleSelect(category: Partial<IDocumentPacketCategory>, listOfDocs: (IPacketDocument | any)[]) {
				// ID could be description for paperwork documents, id for uploaded Documents or id for categories.
				if (listOfDocs.length) this.rawSelectedDocuments[category.id] = listOfDocs;
				else delete this.rawSelectedDocuments[category.id];
			},
			DMVRequestTooltipMessage(): string[] {
				if (this.exceededMaximumRequestAmount) return ['Cannot issue more temporary tags', 'This customer already had 2 temporary tags issued'];

				return this.daysUntilCanResubmit > 0 ? [`${this.daysUntilCanResubmit} days until you may re-submit.`] : [];
			},
			async getMissingPlateDocuments() {
				try {
					this.busyIssuingPlateReg = true;
					const response = await api.plateTransactions.getMissingDocuments(this.fimenu);

					if (response.data.status == 'failure') {
						util.toastr('error', 'Error', response.data?.message ?? 'Failed to retrieve documents.');
					} else {
						util.toastr('success', 'Success', response.data?.message ?? 'Documents retrieval successful.');
						this.fimenu.paperwork.orderPacketDocuments(this.packet);
						this.setPaperworkCategories();
					}
				} catch (err) {
					console.error(err);
				} finally {
					this.busyIssuingPlateReg = false;
				}
			},
			async generateTempTag() {
				$modal.open(modalSetControlNumber, {
					name: 'modalSetControlNumber',
					passedData: {
						plateTransactions: this.fimenu.vehicle.registrationInfo.plateTransactions,
						acceptText: 'Submit',
						cancelText: 'Cancel',
					},
					backdrop: true,
					postFunction: async (ctrlNum: string) => {
						this.fimenu.vehicle.registrationInfo.controlNumber = ctrlNum;
						this.busyIssuingPlateReg = true;

						try {
							const response = await api.plateTransactions.hasCredentials();

							if (!response.data) {
								util.toastr('error', 'Plate Transaction', 'Missing Credentials. Please setup your DDI credentials to proceed.');
								return;
							}

							await IssueTemporaryTag(this.fimenu);
							this.fimenu.paperwork.orderPacketDocuments(this.packet);
							this.setPaperworkCategories();
						} catch (err) {
							console.error('ERROR GENERATING TEMPORARY TAG: ', err);
							util.toastr('error', 'Plate Transaction', 'An error occurred when trying to generate a temporary tag.');
						} finally {
							this.busyIssuingPlateReg = false;
						}
					},
				});

				this.fimenu.paperwork.orderPacketDocuments(this.packet);
				return this.setPaperworkCategories();
			},
			async transferRegPlate() {
				this.busyIssuingPlateReg = true;

				try {
					const response = await api.plateTransactions.hasCredentials();
					if (!response.data) {
						util.toastr('error', 'Plate Transaction', 'Missing Credentials. Please setup your DDI credentials to proceed.');
						return;
					}

					await IssuePlateRegistrationTransfer(this.fimenu);
					this.fimenu.paperwork.orderPacketDocuments(this.packet);
					this.setPaperworkCategories();
				} catch (err) {
					console.error('ERROR TRANSFERRING REGISTRATION PLATE: ', err);
					util.toastr('error', 'Plate Transaction', 'An error occurred when trying to transfer registration plates.');
				} finally {
					this.busyIssuingPlateReg = false;
				}
			},
			convertDateStringToDate(packetDateStamp: string): string {
				//take in a formatted UTC date and output a local date & time
				const formattedDate = moment.utc(packetDateStamp, 'YYYYMMDD_HHmmss').local().format('MM/DD/YYYY hh:mm:ssa');
				return formattedDate == 'Invalid date' ? 'N/A' : formattedDate;
			},
			toggleCollapseExpandAllCategories() {
				const ref = this.richTableRefs;

				if (Array.isArray(ref)) {
					const isAnyTableCollapsed = ref.some(table => {
						return table.data.isCollapsed;
					});

					ref.forEach(tableRef => {
						tableRef.data.isCollapsed = !isAnyTableCollapsed;
					});
				} else if (ref) {
					ref.data.isCollapsed = !ref.data.isCollapsed;
				}
			},
			getUploadedDocuments(): IUploadedDocumentWithMeta[] {
				if (!this.packet.isCurrent) return [];

				const documentsUnsorted: IUploadedDocumentWithMeta[] = [];

				const mostRecentDocList = new Map<string, IUploadedDocumentWithMeta[]>();
				this.docTypesToFilterForRecent.forEach(t => mostRecentDocList.set(t, []));

				this.fimenu.paperwork?.uploadedDocuments?.forEach((doc: IUploadedDocument) => {
					const docType = doc?.docInfo?.docType;

					// Find the document type from settings
					const documentType: IDocumentType = settings.lookups.uploadedDocumentTypes.find((t: IDocumentType) => t.code === docType);

					// Get the document packet category ID, or default to other category description
					const documentPacketCategoryId = documentType?.documentPacketCategoryId ?? this.otherCategoryDescription;

					// Determine the display name for the document type
					const display = docType?.startsWith('CUSTOM_') ? docType.substring(7).replace(/_/g, ' ').toUpperCase() : documentType?.display || 'Other';

					// Add the document to the unsorted list with additional info
					const docWithMeta = { ...doc, documentPacketCategoryId, display };

					// If enabled for this specific doc type, only show the most recent doc of this type.
					if (mostRecentDocList.has(docType)) {
						const currList = mostRecentDocList.get(docType);
						// Find a doc that has a matching association.
						const currDocIndex = currList?.findIndex(c =>
							c.associations.some(cAss => doc.associations.some(dAss => dAss.id === cAss.id && dAss.type === dAss.type)),
						);

						if (currDocIndex === -1)
							currList.push(docWithMeta); // If no doc exist, push this doc to list
						else if (new Date(currList[currDocIndex].docInfo.timestamp) < new Date(doc.docInfo.timestamp)) currList[currDocIndex] = docWithMeta; // Otherwise, replace existing doc

						mostRecentDocList.set(docType, currList);
					} else {
						documentsUnsorted.push(docWithMeta);
					}
				});

				mostRecentDocList.forEach(itemList => {
					if (itemList.length > 0) documentsUnsorted.push(...itemList);
				});

				// Sort the document in alphabetical order.
				return documentsUnsorted.sort((a, b) => {
					return a.display.localeCompare(b.display);
				});
			},
			setPaperworkCategories() {
				this.paperworkCategories = [];

				const uploadedDocuments = this.getUploadedDocuments();

				// Documents that need to be printed & signed on an impact printer or are not eligible for e-signing
				const wetSignDocs = this.packet.documents?.filter(d => isDocWetSignOnly(d)) ?? [];
				if (wetSignDocs.length > 0) {
					this.paperworkCategories.push({
						id: 'WET-DOCS-TO-SIGN',
						description: 'Requires Wet Signature',
						docs: wetSignDocs,
						uploadedDocuments: [],
						isOpen: true,
						isCategory: true,
					});
				}

				settings.lookups.paperworkCategories.forEach((c: any) => {
					const matchingDocs = this.packet.documents?.filter(d => d.categoryId === c.id && !isDocWetSignOnly(d)) ?? [];
					const matchingUploaded = uploadedDocuments?.filter(d => d.documentPacketCategoryId === c.id) ?? [];

					if (matchingDocs.length > 0 || matchingUploaded.length > 0 || (c.pnrDocs && c.pnrDocs.length > 0)) {
						this.paperworkCategories.push({
							...c,
							docs: matchingDocs,
							uploadedDocuments: matchingUploaded,
							isOpen: true,
							isCategory: true,
						});
					}
				});

				// Set "other" category
				const otherDocs =
					this.packet.documents?.filter(d => !this.paperworkCategories.some(c => c.docs.some(catDoc => catDoc.categoryId === d.categoryId))) ?? [];
				const otherUploaded = uploadedDocuments?.filter(d => d.documentPacketCategoryId == null) ?? [];
				if (otherDocs.length > 0 || otherUploaded.length > 0) {
					this.paperworkCategories.push({
						id: this.otherCategoryDescription,
						description: this.otherCategoryDescription,
						docs: otherDocs as IPacketDocument[],
						uploadedDocuments: otherUploaded,
						isOpen: true,
						isCategory: true,
					});
				}
			},
			async downloadDocument(path: string, name: string, configId: string, isCurrent: boolean, isUploadedDoc = false) {
				this.paperworkBusy = true;
				const dealID = this.fimenu.id;
				const uploadedFilePath = path.slice(0, path.lastIndexOf('/'));

				try {
					if (isUploadedDoc) {
						await api.documents.downloadDocument(path, name);
					} else {
						// Repopulate and download only if the package is the current one, has configId and the user is not an spectator
						if (isCurrent && configId && !this.fimenu.isSpectator)
							await api.fimenu.repopulateAndDownloadDocument(path, `${name}.pdf`, dealID, uploadedFilePath);
						else await api.fimenu.downloadDocument(path, name + '.pdf');
					}
				} catch (err) {
					console.error('Error Downloading Document: ', err);
					util.toastr('error', 'Error Downloading Document', 'An unknown server error occurred when trying to download the document.');
				} finally {
					this.paperworkBusy = false;
				}
			},
			handleRemoveDocuments() {
				util.modalConfirmation(`Are you sure you want to DELETE ${this.totalSelectedDocuments} documents from this packet?`, 'Yes', () => {
					try {
						const selectedDocuments = this.flatSelectedDocuments.map(doc => doc.signedDoc ?? doc.filledDoc);
						this.unSelectAll();

						for (let i = this.packet.documents.length - 1; i >= 0; i--) {
							const currDoc = this.packet.documents[i];
							if (selectedDocuments.some(d => d === currDoc.filledDoc || d === currDoc.signedDoc)) this.packet.documents.splice(i, 1);
						}

						this.fimenu.save();
						util.toastr('success', 'Success', 'Document deleted successfully!');
					} catch (err) {
						util.toastr('error', 'Delete Document Failed', (err as Error).message ?? 'Could not delete the selected documents.');
					}
				});
			},
			async handleRefreshPacket() {
				util.modalConfirmation('Are you sure you want to refill this packet? It could include new documents.', 'Yes', async () => {
					const currentPacket = this.fimenu.paperwork.currentPacket();

					try {
						this.isLoading = true;

						// This is a list of all the document configurations that SHOULD/MUST generate this deal.
						//It includes already generated documents
						const docsToGenerateDetails = (await api.fimenu.getDocumentsToGenerate(this.fimenu, true)).data;

						// Send the generation details to actually generate the document.
						for (const generationDetail of docsToGenerateDetails) {
							await this.generateDocument(generationDetail, false);
						}
					} catch (err) {
						console.error(err);
					} finally {
						this.fimenu.updatePaperworkStatus();
						this.fimenu.paperwork.orderPacketDocuments(currentPacket);
						await this.fimenu.save();
						this.isLoading = false;
						util.toastr('success', 'Packet Refreshed', 'Your paperwork was refreshed successfully.');
					}
				});
			},
			async handleRepopulate(openModal = true) {
				const packetSelectedDocuments = this.selectedDocuments.packetDocuments
					.filter(doc => doc.documentGenerationType == ENUMS.DocumentGenerationType.Paperwork)
					.map(doc => doc.signedDoc ?? doc.filledDoc);

				const uploadedSelectedDocuments = this.selectedDocuments.uploadedDocuments.filter(
					doc => doc.documentGenerationType == ENUMS.DocumentGenerationType.Uploaded,
				);

				if (openModal) {
					this.openPreviewPDFModalManyDocs(packetSelectedDocuments, uploadedSelectedDocuments);
				} else {
					this.isLoading = true;
					try {
						await this.downloadMultipleDocuments(packetSelectedDocuments, uploadedSelectedDocuments, true);
						await this.fimenu.save();
					} catch (err) {
						console.error('ERROR DOWNLOADING DOCUMENTS: ', err);
					} finally {
						this.updateRawSelectedDocuments();
						this.isLoading = false;
					}
				}
			},
			updateRawSelectedDocuments() {
				// After repopulating the documents, we need to update the rawSelectedDocuments object since the filledDoc path and signedDoc path might have changed.
				const categoriesToSelect = Object.keys(this.rawSelectedDocuments);
				const docIdsToSelect = Object.values(this.rawSelectedDocuments).flatMap(category => category.map(doc => doc.id));
				const updatedRawSelectedDocs = categoriesToSelect.reduce((acc, category) => {
					acc[category] = this.packet.documents.filter(doc => doc.categoryId === category && docIdsToSelect.includes(doc.id));
					return acc;
				}, {} as any);
				this.rawSelectedDocuments = updatedRawSelectedDocs;
			},
			async openPreviewPDFModalManyDocs(packetSelectedDocuments: string[], uploadedSelectedDocuments: string[]) {
				const sessionInfo = await api.fimenu.getSessionInfo();

				packetSelectedDocuments.forEach(doc => {
					const currentDoc = this.packet?.documents.find(current => current.filledDoc == doc || current.signedDoc == doc);
					const document = this.packet?.documents.find(doc => doc.id == currentDoc.id);

					this.fimenu.addToLog(this.packet.packetDateStamp, 'View', document.description, this.user.EmployeeName, JSON.stringify(sessionInfo.data));
				});

				this.isViewingDoc = true;

				$modal.open(modalPreviewPDF, {
					name: 'modalPreviewPDF',
					passedData: {
						title: 'Documents',
						fimenu: this.fimenu,
						packetSelectedDocuments: packetSelectedDocuments,
						uploadedSelectedDocuments: uploadedSelectedDocuments,
						loadPDFs: this.downloadMultipleDocuments,
					},
					cancelFunction: () => {
						this.isViewingDoc = false;
					},
					backdrop: false,
				});
			},
			async openPreviewPDFModalSingleDoc(docId: string) {
				const docData = this.packet.documents.find(d => d.id === docId);
				const canUploadSignedCopy = this.packet.isCurrent && this.fimenu.dealStatus < ENUMS.DEAL_STATUS.FI_ADMIN_IN_PROCESS && !this.fimenu.isSpectator;
				const sessionInfo = await api.fimenu.getSessionInfo();

				this.fimenu.addToLog(this.packet.packetDateStamp, 'View', docData.description, this.user.EmployeeName, JSON.stringify(sessionInfo.data));

				this.isViewingDoc = true;

				$modal.open(modalPreviewPDF, {
					name: 'modalPreviewPDF',
					passedData: {
						title: 'Documents',
						fimenu: this.fimenu,
						paperworkDocument: docData,
						canUploadSignedCopy: canUploadSignedCopy,
						loadPDFs: this.downloadMultipleDocuments,
						uploadSignedDoc: this.uploadSignedPaperwork,
						removeSignedDoc: this.resetWetSignedStatus,
					},
					cancelFunction: () => {
						this.isViewingDoc = false;
					},
				});
			},
			async uploadSignedPaperwork(docId: string, fileData: any): Promise<IPaperworkDocument> {
				this.isLoading = true;
				try {
					const updatedPaperwork = await this.fimenu.uploadSignedPaperwork(docId, fileData);
					await this.fimenu.save();

					util.toastr('success', 'Upload Success', 'Successfully Uploaded Signed Paperwork.');
					return updatedPaperwork;
				} catch (err) {
					console.error('ERROR UPLOADING SIGNED DOC: ', err);
					util.toastr('error', 'Error Uploading', 'An error occurred while trying to upload the signed paperwork.');
				} finally {
					this.isLoading = false;
				}
			},
			async resetWetSignedStatus(docId: string): Promise<IPaperworkDocument> {
				this.isLoading = true;
				try {
					const updatedPaperwork = this.fimenu.resetWetSignedStatus(docId);
					await this.fimenu.save();

					util.toastr('success', 'Successfully Removed Document', 'Wet-signed paperwork has been successfully removed.');
					return updatedPaperwork;
				} catch (err) {
					console.error('ERROR REMOVING WET-SIGNED DOC: ', err);
					util.toastr('error', 'Error Removing Document', 'An error occurred while trying to remove the wet-signed paperwork.');
				} finally {
					this.isLoading = false;
				}
			},
			async openPreviewSummaryModal(packetSelectedDocuments: any, uploadedSelectedDocuments?: any) {
				const summaryData = { termsText: await this.getSummaryData(this.fimenu) };
				$modal.open(modalLegalTerm, {
					name: 'modalLegalTerm',
					passedData: {
						term: summaryData,
						title: 'Summary',
						printText: 'Print',
					},
					backdrop: false,
				});
			},
			async downloadMultipleDocuments(packetSelectedDocuments: string[] = [], uploadedSelectedDocuments: string[] = [], forceRepopulate = false) {
				try {
					const response = await api.fimenu.repopulateAndDownloadDocuments(this.fimenu.id, packetSelectedDocuments, uploadedSelectedDocuments, forceRepopulate);
					const documentPacket = this.packet;
					// If for whatever reason we can't find the packet. Don't manipulate the data.
					if (!documentPacket) return response?.data?.documentsDictionary;
					// If a document doesn't need to be repopulated anymore. We set it here.
					packetSelectedDocuments.forEach(doc => {
						const currentDoc = documentPacket?.documents.find(current => current.filledDoc == doc || current.signedDoc == doc);

						const responseDocument = response.data.documents.find((responseDoc: any) => responseDoc.id == currentDoc.id);
						if (!responseDocument) return;

						const document = documentPacket?.documents.find(doc => doc.id == currentDoc.id);
						if (document) {
							document.mustRepopulate = responseDocument.mustRepopulate;
							document.signedDoc = responseDocument.signedDoc;
							document.overlays = responseDocument.overlays;
							document.filledDoc = responseDocument.filledDoc;
							document.impactDoc = responseDocument.impactDoc;
							document.error = responseDocument.error;
						}
					});

					return response?.data?.documentsDictionary;
				} catch (err) {
					console.error(err);
					return null;
				}
			},
			async getSummaryData(fimenu: FIMenu) {
				try {
					const response = await api.fimenu.getSummaryData(fimenu);
					return response.data;
				} catch (err) {
					console.error(err);
					return null;
				}
			},
			areAllOverlaysSigned(): boolean {
				return !!this.packet.documents?.every(doc => {
					return (
						doc.isWetSigned ||
						doc.status == ENUMS.PaperworkDocumentStatus.ErrorGenerating ||
						doc.overlays?.length == 0 ||
						doc.overlays?.every(overlay => {
							if (overlay.overlayType === ENUMS.OVERLAY_TYPES.SIGNATURE || overlay.overlayType === ENUMS.OVERLAY_TYPES.INITIALS) {
								return overlay.signature?.isSigned || overlay.initials?.isSigned;
							}
							return true;
						})
					);
				});
			},
			async openCustomerPaperwork() {
				if (!this.user.isAdmin) {
					//Change the ownership of the documents just if is not admin, in case we need to play with production deal
					if (this.fimenu.fiManagerCode != this.user.EmployeeCode) {
						this.fimenu.fiManagerCode = this.user.EmployeeCode;
						this.fimenu.fiManagerName = this.user.EmployeeName;

						// saving so we have the latest fimanager info on the deal
						await this.fimenu.save();

						//repopulate
						this.fimenu.paperwork.currentPacket().documents.forEach((doc: any) => {
							doc.mustRepopulate = true;
						});
					}
				}

				const packetSelectedDocuments: string[] = [];
				this.fimenu.paperwork.currentPacket().documents.forEach((doc: any) => {
					if (doc.mustRepopulate == true) {
						packetSelectedDocuments.push(doc.filledDoc);
					}
				});

				if (packetSelectedDocuments.length != 0) {
					this.isLoading = true;
					await this.downloadMultipleDocuments(packetSelectedDocuments, [], true);
					this.isLoading = false;
				}

				if (!this.areAllOverlaysSigned()) this.EventBus.emit('openCustomerPaperwork');
			},
			async downloadMerged() {
				this.paperworkBusy = true;

				try {
					await api.fimenu.downloadAllDocuments(
						this.fimenu.dealNumber,
						this.fimenu.storeCode,
						this.packet.packetDateStamp,
						`${this.packet.packetDateStamp}.pdf`,
					);
				} catch (err) {
					console.error('ERROR DOWNLOADING MERGED PAPERWORK: ', err);
					util.toastr('error', 'Error Download All Documents', 'An unknown server error occurred when trying to download all documents.');
				} finally {
					this.paperworkBusy = false;
				}
			},
			getDocumentsPanelActions(): any[] {
				const documentsPanelActions = [];

				if (!this.packet.isCurrent) {
					// documentsPanelActions.push({
					// 	text: "\u00A0\u00A0" + "Download Packet",
					// 	class: "button button-icon-text button-info",
					// 	icon: this.paperworkBusy ? "fas fa-cog fa-spin" : "fas fa-download",
					// 	disabled: this.paperworkBusy,
					// 	onClick: this.downloadMerged
					// });

					documentsPanelActions.push({
						text: '\u00A0\u00A0' + 'View',
						class: 'button button-icon-text button-load view-button ignore-all-locks',
						icon: 'far fa-eye',
						disabled: Object.values(this.rawSelectedDocuments).length <= 0 || this.fimenu.isFinalizingPaperwork,
						onClick: this.handleRepopulate,
					});
				}

				if (this.user && this.user.isAdmin && !this.packet.isCurrent) {
					documentsPanelActions.splice(0, 0, {
						text: '\u00A0\u00A0' + 'Restore Packet',
						class: 'button button-icon-text button-save',
						icon: this.paperworkBusy ? 'fas fa-cog fa-spin' : 'fas fa-retweet',
						disabled:
							!this.restorePacket ||
							this.paperworkBusy ||
							!this.fimenu.coverageTerms.some(c => c.selected) ||
							this.fimenu.paperwork.packets.some((p: any) => p.isCurrent),
						onClick: () => this.restorePacket(this.packet),
					});
				}

				return documentsPanelActions;
			},
			async getCustomersCreditReport() {
				const customer = this.fimenu.customer;
				if (customer?.id) {
					const response = await api.creditreport.getByCustomerId(customer.id, this.fimenu.isDealLocked() ? this.fimenu.id : null);

					if (response.data != null) {
						this.fimenu.customer.creditReport = new FIMenuCreditReport(response.data);
					}
				}

				const coCustomer = this.fimenu.coCustomer;
				if (coCustomer?.id) {
					const response = await api.creditreport.getByCustomerId(coCustomer.id, this.fimenu.isDealLocked() ? this.fimenu.id : null);

					if (response.data != null) {
						this.fimenu.coCustomer.creditReport = new FIMenuCreditReport(response.data);
					}
				}
			},
			async voidPaperworkAction(isNonApi = false) {
				if (this.$dealRoomHub.connected && this.fimenu.isSpectator) return;

				if (isNonApi) {
					this.voidPaperwork(isNonApi);
					return;
				} else {
					this.isLoading = true;
					const anyChargebacks = await notifyIfAnyDealChargebacks(this.fimenu);
					this.isLoading = false;

					if (anyChargebacks) return;
				}

				const timezone = util.getStoreTimezone(this.$global.selectedStore.storeTimeZone);

				// If the packet is from the current month, let them proceed, otherwise don't
				const packetDateStr = moment.utc(this.packet.packetDateStamp, 'YYYYMMDD_hhmmss').toDate().toLocaleString('en-US', { timeZone: timezone });
				const packetDate = moment(packetDateStr, 'MM/DD/YYYY hh:mm:ss A');

				// console.log("packet date", packetDate)

				const todayStoreDateStr = moment().utc().toDate().toLocaleString('en-US', { timeZone: timezone });
				const todayStoreDate = moment(todayStoreDateStr, 'MM/DD/YYYY hh:mm:ss A');

				// console.log("todays store date.", todayStoreDate)

				const foundADocWithContractNumber = this.packet.documents.some(a => a.contractNumber != null);
				if ((todayStoreDate.diff(packetDate, 'years') == 0 && todayStoreDate.month() == packetDate.month()) || !foundADocWithContractNumber) {
					$modal.open(modalInfo, {
						name: 'modalInfo',
						passedData: {
							info: 'Are you sure you want to void current paperwork?',
							cancelText: 'Cancel',
							acceptText: 'Continue',
						},
						backdrop: false,
						postFunction: this.voidPaperwork,
					});
				} else {
					if (this.user && this.user.hasAccountingDepartmentAccess) {
						$modal.open(modalInfo, {
							name: 'modalInfo',
							passedData: {
								info: "The Products you are attempting to cancel are from a previous month. The VOID function might not void in the Provider's portal, and you might still need to flat cancel the Products. Click Continue to remove these Products from Saturn, you will still need to check these products in their Provider's portal.",
								cancelText: 'Cancel',
								acceptText: 'Continue',
							},
							backdrop: false,
							postFunction: this.voidPaperwork,
						});
					} else {
						$modal.open(modalInfo, {
							name: 'modalInfo',
							passedData: {
								info: 'This is a packet from a previous month and cannot be voided through Saturn. Please ask accounting to Flat Cancel the coverage before re-issuing new coverage.',
								acceptText: 'Continue',
							},
							backdrop: true,
						});
					}
				}
			},
			async voidPaperwork(isNonApi = false) {
				if (
					this.fimenu.dealJacket.eContract != null &&
					this.fimenu.dealJacket.eContract.status >= EContractStatus.DistributionInProgress &&
					this.fimenu.dealJacket.eContract.status != EContractStatus.Funded
				) {
					util.toastr('error', 'Voiding', 'This deal is in distribution. Voiding is not allowed.');
					return;
				}

				// if (this.fimenu.dealJacket.eContract.status != EContractStatus.Funded) {
				// >> Request evidence <<
				// }

				this.fimenu.buyersOrderUpdateRequired = false;

				this.setVoidedPacket();
				this.paperworkBusy = true;
				this.busyVoiding = true;
				this.modalStatus.modalBusy = true;

				this.EventBus.emit('paperworkStartVoiding');
				try {
					const response = await api.fimenu.voidPaperwork(this.fimenu, isNonApi);

					if (response && response.data) this.fimenu.paperwork = new FIMenuPaperwork(response.data);
					this.EventBus.emit('paperworkReset');

					// We check within the method if there's anything to be voided.
					await this.voidPlateTransactions();

					if (this.fimenu.customer.id == null) {
						this.fimenu.customer.isThereAnyUnsavedCustomerChanges = true;
					}

					if (this.fimenu.hasCoSigner && this.fimenu.coCustomer.id == null) {
						this.fimenu.coCustomer.isThereAnyUnsavedCustomerChanges = true;
					}

					// Attempt to update all vehicles in deal in inventory
					if (!isNonApi) await updateInventory(this.fimenu, true);
				} catch (err) {
					console.error(err);
				} finally {
					this.paperworkBusy = false;
					this.busyVoiding = false;
					this.modalStatus.modalBusy = false;

					this.EventBus.emit('paperworkEndedVoiding');
					this.getCustomersCreditReport();
				}
			},
			async voidPlateTransactions() {
				const isTransferDeal = this.fimenu.vehicle.registrationInfo.isPlateTransfer;
				const lastTransferTransaction = this.fimenu.vehicle.registrationInfo?.getLatestTransfer();
				const hasPreviousTransferDealThatIsNotVoid = lastTransferTransaction?.isTransferVoid == false;
				const isPlateTransactionsEnabled = !!IsPlateTransactionsEnabled(this.fimenu);

				if (isPlateTransactionsEnabled && isTransferDeal && hasPreviousTransferDealThatIsNotVoid && lastTransferTransaction != null) {
					try {
						// This method will also add the new transaction into FIMenu
						const response = await CancelPlateTransaction(lastTransferTransaction.providerDealId, this.fimenu.id);
						const tIndex = this.fimenu.vehicle.registrationInfo.plateTransactions.findIndex(x => x.providerDealId == lastTransferTransaction.providerDealId);

						if (tIndex >= 0 && response.data.payload) {
							const transaction = new FIMenuPlateTransactions(response.data.payload);
							this.fimenu.vehicle.registrationInfo.plateTransactions[tIndex] = transaction;

							if (transaction.error.errorMessage) {
								util.toastr('error', 'Plate Transaction', transaction.error.errorMessage);
							} else {
								util.toastr('success', 'Plate Transaction', 'Transaction voided successfully.');
							}
						}
					} catch (err) {
						util.toastr('error', 'Plate Transaction', err ?? 'We could not void the registration.');
					}
				}
			},
			async activatePaperwork(docToActivate: IPacketDocument = null) {
				if (this.fimenu.isAvidan() && settings.environmentName != 'PRODUCTION') {
					if (
						!window.confirm(
							"You are using AVIDAN's PHONE NUMBNER in Customer Cell (this will activate with PRODUCTION credentials and create real contracts). Are you sure you want to continue?",
						)
					)
						return;
				}

				this.paperworkBusy = true;
				this.busyActivating = true;
				this.modalStatus.modalBusy = true;
				this.EventBus.emit('paperworkStartActivating');

				const activatePaperworkRequest = {
					fimenu: this.fimenu,
					docTypes: docToActivate ? [docToActivate.type] : [],
				};

				try {
					const response = await api.fimenu.activatePaperwork(activatePaperworkRequest);
					if (!response?.data) throw new Error('No server response.', { cause: response });
					if (response?.data?.error) throw new Error(response.data.error.description, { cause: response });

					const activatedDocs = response.data;

					activatedDocs.forEach((doc: IPacketDocument) => {
						const index = this.packet.documents.findIndex(d => d.type == doc.type && d.modelId == doc.modelId);

						/* eslint-disable no-console */
						console.log('Packet before', this.packet.documents[index].activationInfo, doc);
						this.packet.documents[index] = doc;
						console.log('Packet after', this.packet.documents[index].activationInfo);
						/* eslint-enable no-console */
					});

					util.toastr('success', 'Activation', 'Activation completed.');
				} catch (err) {
					console.error('Error Activating Paperwork: ', err);
					util.toastr('error', 'Activation', (err as Error)?.message ?? 'An unknown server error occurred.');
				} finally {
					this.paperworkBusy = false;
					this.busyActivating = false;
					this.modalStatus.modalBusy = false;
					this.EventBus.emit('paperworkEndedActivating');
					this.setPaperworkCategories();
				}
			},
			ignoreDelayActivate() {
				if (this.$global.isAdminView) {
					if (!window.confirm('Selected contracts will ingore activation delay and activate. Are you sure you want to continue?')) return;
				}

				for (const document of this.flatSelectedDocuments) {
					if (document.activationInfo === null) {
						const docToUpdate = this.packet.documents.find(doc => doc.id == document.id);
						docToUpdate.activationInfo = { activatedBy: 'Ignore Delay' };
					}
				}
				this.activatePaperwork();
			},
			openEditContractNumber(doc: IPacketDocument | any) {
				if (!this.$global.isAdminView) return;

				$modal.open(modalEditContractNumber, {
					name: 'modalEditContractNumber',
					passedData: { doc, acceptText: 'Save', cancelText: 'Cancel' },
					user: this.user,
					backdrop: true,
					postFunction: () => this.handleModalEditContractNumberSave(doc),
				});
			},
			handleModalEditContractNumberSave(doc: IPacketDocument | any) {
				const { PaperworkDocumentStatus: pwDocSatus } = ENUMS;
				const { documents } = this.packet;

				if (!doc.contractNumber && !doc.memberNumber && doc.activationInfo?.activationError) {
					doc.status = pwDocSatus.ErrorActivating;
				} else if (doc.status === pwDocSatus.ErrorActivating) {
					if (doc.signedDoc) {
						doc.status = pwDocSatus.Signed;
					} else {
						const index = documents.findIndex(d => doc.modelId === d.modelId && doc.type === d.type);

						if (index > -1 && documents.length >= index && documents[index].status === pwDocSatus.SigningInProgress) {
							doc.status = pwDocSatus.SigningInProgress;
						} else {
							doc.status = pwDocSatus.ReadyToBeSigned;
						}
					}
				}

				doc.activationInfo = {
					activatedBy: 'Administrator',
					activationDateStamp: moment.utc(),
					activationError: null,
					isSuccess: true,
				};

				this.EventBus.emit('paperworkEndedActivating');
			},
			getProductDetails(doc: IPacketDocument | any): string[] {
				if ([ENUMS.PAPERWORK_DOCUMENT_TYPES.PaperworkDocument, ENUMS.PAPERWORK_DOCUMENT_TYPES.AcceptDecline].includes(doc.type)) {
					return [];
				}

				const displayDetails = [];
				const documentProductType = ENUMS.paperworkDocumentTypes.find(paperworkDocumentType => paperworkDocumentType.typeString == doc.typeString);

				if (!documentProductType) return [];

				const productDetails = this.activeProducts.find(
					product => product.productType === documentProductType.productType && product.productPricingDetailsId === doc.modelId,
				);

				if (!productDetails) return [];

				if (productDetails.productInfo?.program?.description) displayDetails.push(`${productDetails.productInfo.program.description}`);

				if (productDetails.productInfo?.termLimit) displayDetails.push('', `${Number(productDetails.productInfo.termLimit) / 12} years`);

				if (productDetails.productInfo?.milesLimit) displayDetails.push('', `${productDetails.productInfo.milesLimit} miles`);

				if (productDetails.productPricing?.cost && productDetails.productPricing?.price) {
					displayDetails.push(
						'',
						`Cost: ${util.formatPrice(productDetails.productPricing.cost)}`,
						'',
						`Price: ${util.formatPrice(productDetails.productPricing.price)}`,
						'',
						`Profit: ${util.formatPrice(Number(productDetails.productPricing.price) - Number(productDetails.productPricing.cost))}`,
					);
				}

				return displayDetails.length > 0 ? displayDetails : [];
			},
			getPaperworkDocType(doc: IPacketDocument): {
				typeString?: string;
				productType?: number;
				provider?: string;
			} {
				let docType = ENUMS.paperworkDocumentTypes.find(pdt => pdt.typeString == doc.typeString);
				if (!docType) return { provider: doc.typeString };

				docType = util.clone(docType);
				if (docType.typeString.includes('PEN')) {
					if (doc.modelId == util.getDefaultGuid()) {
						docType.provider = this.fimenu.selectedProviders.find((sp: any) => sp.productType === docType.productType)?.provider;
					} else {
						const mp = this.fimenu.miscellaneousProviders.find(mp => mp.productPricingDetailsId === doc.modelId);

						if (mp.provider) {
							docType.provider = mp.provider;
						} else if (mp.productInfoAndPricing.length > 0) {
							docType.provider = mp.productInfoAndPricing[0].termInfo.provider;
						} else {
							docType.provider = mp.productInfo.provider;
						}
					}

					docType.provider = docType.provider ? docType.provider.substring(4) : 'PEN';
				}

				return docType;
			},
			signedInformation(doc: IPacketDocument | any): string[] {
				if (doc.isWetSigned) return [`Wet-signed copy uploaded at ${this.convertDateStringToDate(doc.wetSignedTimestamp)}`];

				const messages = [];
				const customerSigned = isDocSignedBySignerOrDone(doc, this.fimenu.customer.id);

				if (customerSigned) {
					const formattedTimestamp = this.convertDateStringToDate(this.getSignedDate(doc.overlays, ENUMS.PERSON_TYPES.CUSTOMER));
					const fullName = this.fimenu.customer?.fullName?.toUpperCase();

					messages.push(`${fullName} signed at ${formattedTimestamp}`);
				}

				if (this.fimenu.hasCoSigner) {
					const coCustomerSigned = isDocSignedBySignerOrDone(doc, this.fimenu.coCustomer.id);

					if (coCustomerSigned) {
						if (customerSigned) messages.push('');

						const formattedTimestamp = this.convertDateStringToDate(this.getSignedDate(doc.overlays, ENUMS.PERSON_TYPES.CUSTOMER));
						const fullName = this.fimenu.coCustomer?.fullName?.toUpperCase();

						messages.push(`${fullName} signed at ${formattedTimestamp}`);
					}
				}

				return messages;
			},
			getSignedDate(overlays: IDocumentOverlay[], signer: number): string {
				if (overlays?.length < 1) return null;

				const signedTimeStamps = overlays?.map(overlay => {
					if (overlay.signature?.personType == signer || overlay.initials?.personType == signer) {
						return overlay.signature?.signedSessionInfo?.signedTimestamp ?? overlay.initials?.signedSessionInfo?.signedTimestamp;
					}
				});
				signedTimeStamps?.sort((a, b) => Number(new Date(b)) - Number(new Date(a)));

				if (!signedTimeStamps || signedTimeStamps.length < 1) return null;
				return signedTimeStamps[0]?.toString();
			},
			tooltipInformation(doc: IPacketDocument): string[] {
				if (doc.status == ENUMS.PaperworkDocumentStatus.ErrorGenerating) return [];

				const messages: string[] = [];

				const _generateActivationInformation = () => {
					if (messages.length) messages.push('-');

					if (doc.activationInfo?.activationError) {
						messages.push('Activation Error: ' + doc.activationInfo.activationError.errorDescription);
					}

					if (doc.activationInfo?.activatedBy) {
						if (messages.length) messages.push('');
						messages.push(`Activated by ${doc.activationInfo.activatedBy}`);
					}

					if (doc.activationInfo?.activationDateStamp) {
						if (messages.length) messages.push('');
						messages.push(`Activated at ${moment.utc(doc.activationInfo.activationDateStamp, 'YYYYMMDD_HHmmss').local().format('MM/DD/YYYY hh:mm:ssa')}`);
					}

					if (doc.voidInfo?.voidError || doc.activationInfo?.activationError || doc.status == ENUMS.PaperworkDocumentStatus.Voided) {
						if (messages.length) messages.push('');
						if (doc.memberNumber && doc.contractNumber) messages.push(`Member/Contract number: ${doc.memberNumber}/${doc.contractNumber}`);
						else if (doc.memberNumber) messages.push(`Member number: ${doc.memberNumber}`);
						else if (doc.contractNumber) messages.push(`Contract number: ${doc.contractNumber}`);
					}
				};

				const _generateVoidInformation = () => {
					if (doc.voidInfo?.voidError) {
						if (messages.length) messages.push('');
						messages.push('Void Error: ' + doc.voidInfo.voidError.errorDescription);
					}

					if (doc.voidInfo?.voidedBy) {
						if (messages.length) messages.push('');
						messages.push(`Voided by ${doc.voidInfo.voidedBy}`);
					}

					if (doc.voidInfo?.voidDateStamp) {
						if (messages.length) messages.push('');
						messages.push(`Voided at ${moment.utc(doc.voidInfo.voidDateStamp, 'YYYYMMDD_HHmmss').local().format('MM/DD/YYYY hh:mm:ssa')}`);
					}
				};

				const _generateCancelInformation = () => {
					if (doc.cancellationInfo?.error) {
						if (messages.length) messages.push('');
						messages.push('Cancel Error: ' + doc.cancellationInfo.error.errorDescription);
					}

					if (doc.cancellationInfo?.requestedBy) {
						if (messages.length) messages.push('');
						messages.push(`Cancelled by ${doc.cancellationInfo.requestedBy}`);
					}

					if (doc.cancellationInfo?.dateStamp) {
						if (messages.length) messages.push('');
						messages.push(`Cancelled at ${moment.utc(doc.cancellationInfo.dateStamp, 'YYYYMMDD_HHmmss').local().format('MM/DD/YYYY hh:mm:ssa')}`);
					}
				};

				_generateCancelInformation();
				_generateVoidInformation();
				_generateActivationInformation();

				if (messages[messages.length - 1] == '-') messages.splice(messages.length - 1, 1);

				return messages;
			},
			async regenerateSelected() {
				util.modalConfirmation("Are you sure you want to regenerate the selected documents? You will lose any signatures if it's signed.", 'Yes', async () => {
					try {
						this.isLoading = true;
						await this.fimenu.save();

						for (const document of this.flatSelectedDocuments) {
							await this.regenerateDocument(document, [], true);
						}

						this.unSelectAll();
					} finally {
						this.isLoading = false;
					}
				});
			},
			async regenerateDocument(document: IPacketDocument, docsToGenerateDetails: any[] = [], forceInternal = false) {
				const currentPacket = this.fimenu.paperwork.currentPacket();

				try {
					this.regeneratingAndDownloadMultipleDocs = true;
					let docToGenerateDetails = null;

					const nonProductDocTypes = [
						ENUMS.PAPERWORK_DOCUMENT_TYPES.PaperworkDocument,
						ENUMS.PAPERWORK_DOCUMENT_TYPES.AcceptDecline,
						ENUMS.PAPERWORK_DOCUMENT_TYPES.Trades,
						ENUMS.PAPERWORK_DOCUMENT_TYPES.CarFax,
					];

					if (!nonProductDocTypes.includes(document.type)) {
						// Get all the documents details that are needed in this deal.

						// TODO: Improve to only get the generation details of the required document. We're getting all because we're reusing this method.
						if (!docsToGenerateDetails.length) docsToGenerateDetails = (await api.fimenu.getDocumentsToGenerate(this.fimenu)).data;

						// Find the detail that matches the document i'm trying to generate.
						docToGenerateDetails = docsToGenerateDetails.find(x => {
							const productType = ENUMS.paperworkDocumentTypes.find(dt => dt.typeString === document.typeString)?.productType;
							const matchProduct = x.productDetails?.productType == productType && x.productDetails?.productPricingDetailsId == document.modelId;
							return matchProduct;
						});
					} else if (document.paperworkDocumentConfigId != null) {
						// If it's a non-product document.
						const response = await api.fimenu.getLastestDocConfig(this.fimenu, document.paperworkDocumentConfigId, document.modelId);
						if (response.data) docToGenerateDetails = response.data;
					}

					// Some documents cannot be regenerated because if the criteria formula to generate it changed. There's no way for us know which document has to be regenerated.
					// In the front-end when we only have the product info within the document.
					if (docToGenerateDetails == null) {
						throw new Error('Document cannot be regenerated!');
					}

					const succeeded = await this.generateDocument(docToGenerateDetails, forceInternal, document);

					if (succeeded) util.toastr('success', 'Success', 'Document generated successfully!');
				} catch (err) {
					util.toastr('error', 'Error', (err as Error).message ?? 'Document could not be generated!');
				} finally {
					this.fimenu.paperwork.orderPacketDocuments(currentPacket);
					this.regeneratingAndDownloadMultipleDocs = false;
				}
			},
			// This method takes in a generation details and generates them one.
			async generateDocument(docToGenerateDetails: any, forceInternal: boolean, document: IPacketDocument = null): Promise<boolean> {
				// Send the generation details to actually generate the document.
				const { data: responseData } = await api.fimenu.generateDocument(this.fimenu, docToGenerateDetails, forceInternal);
				const generatedDocument = responseData.payload;

				let isSuccess = true;
				if (responseData.statusCode >= 400) {
					util.toastr('error', 'Error', responseData?.messages[0] ?? 'Unexpected server error.');
					isSuccess = false;
				}

				if (!generatedDocument) {
					return false;
				}

				// Populate the document in the document packet.
				const currentPacket = this.fimenu.paperwork.currentPacket();
				if (currentPacket) {
					const documentIndex = currentPacket.documents.findIndex((indexDoc: any) => indexDoc.id == document?.id);
					if (documentIndex >= 0) {
						// If we're using internal only. That means we're regenerating the document; which will update the contracts numbers to the new document.
						if (forceInternal) {
							generatedDocument.contractRate = currentPacket.documents[documentIndex]?.contractRate;
							generatedDocument.contractNumber = currentPacket.documents[documentIndex]?.contractNumber;
							generatedDocument.memberNumber = currentPacket.documents[documentIndex]?.memberNumber;
							generatedDocument.activationInfo = currentPacket.documents[documentIndex]?.activationInfo;
						}

						currentPacket.documents.splice(documentIndex, 1, generatedDocument);
					} else {
						currentPacket.documents.push(generatedDocument);
					}

					await this.fimenu.save();
				}

				return isSuccess;
			},
		},
	});
</script>
<style>
	.document-packet #select-all-checkbox {
		margin-right: 12px;
	}
	.document-packet-subtitle {
		padding: 10px;
	}
	.splitlines {
		display: flex;
		flex-direction: column;
		row-gap: 2px;
	}

	.signersText {
		font-size: 10.5pt;
		font-weight: bold;
		color: #fda362;
	}

	.signed,
	.text-success {
		color: var(--success-color);
	}

	.text-error {
		color: var(--error-color);
	}

	.text-warning {
		color: #fda362;
	}

	.bold-text {
		font-weight: bold;
	}

	.messageContainer {
		box-sizing: border-box;
		cursor: default;
		user-select: none;
	}

	.messageContainer p {
		font-size: 11pt !important;
	}

	.defaultCursor {
		cursor: default;
	}

	.iconSpacing {
		margin-right: 6px;
	}

	.rich-table-header.docName-header,
	.rich-table-header.signature-header,
	.rich-table-header.download-header,
	.rich-table-header.activation-header {
		vertical-align: middle !important;
	}
	.docName-header {
		width: 35%;
	}

	.signature-header {
		width: 30%;
	}
	.download-header {
		width: 8%;
	}
	.activation-header {
		width: 20%;
	}

	.rich-table-header-checkbox {
		width: 100px;
	}

	.rich-table-rows-checkbox .InputCheckbox {
		margin-left: 20px;
	}

	.has-field-error > button {
		border: 2px solid var(--print-color);
		border-radius: 5px;
	}

	.button.button-icon-text.button-load {
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.document-packet .rich-table-container {
		max-height: unset !important;
	}

	.document-packet table.rich-table tbody {
		text-transform: unset;
	}

	.border-left-row {
		position: relative;
		background-color: var(--black-10percent) !important;
		/* border-left: 5px solid blue; */
	}
	.border-left-row::after {
		content: '';
		position: absolute;
		width: 5px;
		height: 100%;
		left: 0px;
		background: blue;
		/* border-left: 5px solid blue; */
	}
</style>
